import { useState } from "react";
import { ChevronDown, Lock, CalendarClock, ShieldAlert } from "lucide-react";
import {
  User, Backpack, Landmark, Map, UsersRound, Coins, Store,
  ScrollText, PawPrint, Building2, Trophy, MessageSquare,
  Home, Shield, Medal, BookMarked, Swords, Sword, Sparkles, Skull, Globe2, Users,
  Flag as FlagIcon,
  // Skill glyphs — kept in the same line-icon family as the rest of the dock
  // so the sidebar reads as one coherent set rather than "menu icons + a
  // strip of painted illustrations".
  TreePine, Pickaxe, Fish, Sprout, Rabbit, CookingPot, Flame, Hammer,
  Feather, Scissors, Leaf, Wand2, Wind, Fingerprint, Heart, Target,
  HandHeart, Zap,
} from "lucide-react";
import { SKILL_LABEL } from "@/lib/gameData";

// Skill → line-icon mapping. Every one is a lucide-react glyph so the icon
// stroke weight, cap style and sizing all match the Character / Combat / Play
// items exactly.
const SKILL_ICON = {
  woodcutting:  TreePine,
  mining:       Pickaxe,
  fishing:      Fish,
  farming:      Sprout,
  hunter:       Rabbit,
  cooking:      CookingPot,
  firemaking:   Flame,
  smithing:     Hammer,
  fletching:    Feather,
  crafting:     Scissors,
  herblore:     Leaf,
  runecrafting: Sparkles,
  agility:      Wind,
  thieving:     Fingerprint,
  slayer:       Skull,
  construction: Home,
  attack:       Swords,
  strength:     Zap,
  defence:      Shield,
  hitpoints:    Heart,
  ranged:       Target,
  magic:        Wand2,
  prayer:       HandHeart,
};

// -- Config ---------------------------------------------------------------

const CHARACTER_ITEMS = [
  { id: "profile",   label: "Profile",   icon: User },
  { id: "inventory", label: "Inventory", icon: Backpack },
  { id: "equipment", label: "Equipment", icon: Shield },
  { id: "bank",      label: "Bank",      icon: Landmark },
  { id: "map",       label: "Map",       icon: Map },
  { id: "country",   label: "Country",   icon: FlagIcon },
];

const PARTY_ITEMS = [
  { id: "party", label: "Party", icon: UsersRound,
    locked: { kind: "total_level", value: 50 } },
];

// Every skill in the sidebar routes to the Skills panel; each row shows the
// live Lv. badge for the referenced skill. Some are locked by total level.
const SKILL_ITEMS = [
  { skill: "woodcutting",   panel: "skills" },
  { skill: "mining",        panel: "skills" },
  { skill: "fishing",       panel: "skills" },
  { skill: "farming",       panel: "skills" },
  { skill: "hunter",        panel: "skills" },
  { skill: "cooking",       panel: "skills" },
  { skill: "firemaking",    panel: "skills" },
  { skill: "smithing",      panel: "skills" },
  { skill: "fletching",     panel: "skills" },
  { skill: "crafting",      panel: "skills" },
  { skill: "herblore",      panel: "skills" },
  { skill: "runecrafting",  panel: "skills" },
  { skill: "agility",       panel: "skills" },
  { skill: "thieving",      panel: "skills" },
  { skill: "slayer",        panel: "skills" },
  { skill: "construction",  panel: "skills" },
];

const ECONOMY_ITEMS = [
  { id: "exchange",      label: "Grand Exchange", icon: Coins },
  { id: "general_store", label: "General Store",  icon: Store },
];

const COLLECTION_ITEMS = [
  { id: "pets",         label: "Pets",           icon: PawPrint },
  { id: "museum",       label: "Museum",         icon: Building2 },
  { id: "housing",      label: "Housing",        icon: Home },
];

const ADVENTURE_ITEMS = [
  { id: "quests",       label: "Quests",         icon: ScrollText },
  { id: "achievements", label: "Achievements",   icon: Medal },
  { id: "events",       label: "Events",         icon: CalendarClock },
];

const SOCIAL_ITEMS = [
  { id: "chat",         label: "Chat",           icon: MessageSquare },
  { id: "friends",      label: "Friends",        icon: Users },
  { id: "clans",        label: "Clans",          icon: Shield },
  { id: "leaderboard",  label: "Leaderboard",    icon: Trophy },
];

const COMBAT_ITEMS = [
  { id: "combat",       label: "Battle",       icon: Swords,
    highlightSkill: "attack", panel: "combat" },
  { id: "arena",        label: "Arena",        icon: Sword },
  { id: "wilderness",   label: "The Wilds",    icon: Flame },
  { id: "dungeons",     label: "Dungeons",     icon: Skull },
  { id: "worldbosses",  label: "World Bosses", icon: Globe2 },
];

// -- Rows -----------------------------------------------------------------

function SkillRow({ skill, active, onClick, level }) {
  const Icon = SKILL_ICON[skill] || Sparkles;
  return (
    <button
      onClick={onClick}
      data-testid={`nav-skill-${skill}`}
      title={`${SKILL_LABEL[skill]} · Level ${level}`}
      className={
        "w-full flex items-center gap-3 px-3 py-1.5 rounded transition-colors duration-150 " +
        (active
          ? "bg-amber-900/40 text-amber-200 border border-amber-700"
          : "text-stone-300 hover:bg-stone-800/60 hover:text-amber-200 border border-transparent")
      }
    >
      <Icon size={18} strokeWidth={1.6} />
      <span className="hidden lg:inline font-body text-sm flex-1 text-left">{SKILL_LABEL[skill]}</span>
      <span
        className="hidden lg:flex text-[10px] font-numeric text-amber-300 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5"
        data-testid={`nav-skill-${skill}-lv`}
      >
        Lv. {level}
      </span>
    </button>
  );
}

function GenericRow({ item, active, onClick, totalLevel }) {
  const Icon = item.icon;
  const locked = !!item.locked;
  const okLevel = item.locked ? totalLevel >= item.locked.value : true;
  const disabled = locked && !okLevel;
  const indent = !!item.indent;
  return (
    <button
      onClick={() => !disabled && onClick(item.id)}
      disabled={disabled}
      data-testid={`nav-${item.id}`}
      title={disabled ? `Unlocks at Total Lv. ${item.locked.value}` : item.label}
      className={
        "w-full flex items-center gap-3 py-1.5 rounded transition-colors duration-150 " +
        (indent ? "pl-7 pr-3 " : "px-3 ") +
        (active
          ? "bg-amber-900/40 text-amber-300 border border-amber-700"
          : disabled
            ? "text-stone-600 cursor-not-allowed"
            : "text-stone-300 hover:text-amber-300 hover:bg-stone-800/50 border border-transparent")
      }
    >
      <Icon size={18} strokeWidth={1.6} className={disabled ? "opacity-40" : ""} />
      <div className="hidden lg:flex flex-1 min-w-0 flex-col items-start leading-tight">
        <span className="font-body text-sm truncate">{item.label}</span>
        {disabled && (
          <span className="text-[10px] font-body text-stone-600">
            Unlocks at Total Lv. {item.locked.value}
          </span>
        )}
      </div>
      {!disabled ? (
        item.badge != null && (
          <span className="hidden lg:inline text-[10px] font-numeric text-amber-300 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5">
            {item.badge}
          </span>
        )
      ) : (
        <Lock size={12} className="hidden lg:inline text-stone-600" />
      )}
    </button>
  );
}

function Section({ id, label, defaultOpen = true, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid || `nav-section-${id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="hidden lg:flex w-full items-center justify-between px-3 py-1 text-[10px] uppercase tracking-widest font-numeric text-stone-500 hover:text-amber-500 transition-colors"
      >
        <span>{label}</span>
        <ChevronDown size={12} className={open ? "" : "-rotate-90 transition-transform"} />
      </button>
      {open && <div className="space-y-0.5 mt-1">{children}</div>}
    </div>
  );
}

// -- Main -----------------------------------------------------------------

export function LeftDock({ current, currentSkill, onChange, totalLevel = 0, skills = {}, isAdmin = false }) {
  return (
    <aside
      className="w-16 lg:w-56 panel flex-shrink-0 flex flex-col overflow-y-auto scroll-thin"
      style={{ borderRadius: 0, borderTop: "none", borderBottom: "none", borderLeft: "none" }}
      data-testid="left-dock"
    >
      <nav className="flex-1 py-3 px-2 space-y-3">
        {/* Character */}
        <Section id="character" label="Character">
          {CHARACTER_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Combat */}
        <Section id="combat" label="Combat">
          {COMBAT_ITEMS.map((it) => {
            if (it.highlightSkill) {
              const lv = skills?.[it.highlightSkill]?.level ?? 1;
              const targetPanel = it.panel || it.id;
              return (
                <button
                  key={it.id}
                  onClick={() => onChange(targetPanel, { focus: it.highlightSkill })}
                  data-testid={`nav-${it.id}`}
                  className={
                    "w-full flex items-center gap-3 px-3 py-1.5 rounded transition-colors duration-150 " +
                    (current === targetPanel
                      ? "bg-amber-900/40 text-amber-300 border border-amber-700"
                      : "text-stone-300 hover:text-amber-300 hover:bg-stone-800/50 border border-transparent")
                  }
                >
                  <it.icon size={18} strokeWidth={1.6} className="text-red-400" />
                  <span className="hidden lg:inline font-body text-sm flex-1 text-left">{it.label}</span>
                  <span className="hidden lg:inline text-[10px] font-numeric text-amber-300 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5">
                    Lv. {lv}
                  </span>
                </button>
              );
            }
            return <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />;
          })}
        </Section>

        {/* Party */}
        <Section id="party" label="Party">
          {PARTY_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Skills */}
        <Section id="skills" label="Skills">
          {SKILL_ITEMS.map(({ skill }) => (
            <SkillRow
              key={skill}
              skill={skill}
              active={current === "skill" && currentSkill === skill}
              onClick={() => onChange("skill", { skill })}
              level={skills?.[skill]?.level ?? 1}
            />
          ))}
        </Section>

        {/* Economy */}
        <Section id="economy" label="Economy">
          {ECONOMY_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Collection */}
        <Section id="collection" label="Collection">
          {COLLECTION_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Adventure */}
        <Section id="adventure" label="Adventure">
          {ADVENTURE_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Social */}
        <Section id="social" label="Social">
          {SOCIAL_ITEMS.map((it) => (
            <GenericRow key={it.id} item={it} active={current === it.id} onClick={onChange} totalLevel={totalLevel} />
          ))}
        </Section>

        {/* Admin — always visible; the backend enforces the actual gating */}
        <Section id="admin" label="Live Ops">
          <GenericRow
            item={{ id: "admin", label: "Admin", icon: ShieldAlert }}
            active={current === "admin"}
            onClick={onChange}
            totalLevel={totalLevel}
          />
        </Section>
      </nav>
      <div className="hidden lg:block px-4 py-3 border-t border-stone-800 text-[10px] text-stone-600 uppercase tracking-widest font-numeric">
        Tick 600ms
      </div>
    </aside>
  );
}


export function MobileDock({ current, onChange }) {
  const FIVE = [
    { id: "profile",   label: "Profile",  icon: User },
    { id: "skills",    label: "Skills",   icon: Sparkles },
    { id: "combat",    label: "Combat",   icon: Swords },
    { id: "exchange",  label: "GE",       icon: Coins },
    { id: "housing",   label: "House",    icon: Home },
  ];
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 panel flex justify-around py-2"
      style={{ borderRadius: 0, borderBottom: "none", borderLeft: "none", borderRight: "none" }}
      data-testid="mobile-dock"
    >
      {FIVE.map(({ id, label, icon: Icon }) => {
        const active = current === id;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            data-testid={`mobile-nav-${id}`}
            className={"flex flex-col items-center gap-0.5 px-2 py-1 " + (active ? "text-amber-300" : "text-stone-500")}
          >
            <Icon size={18} strokeWidth={1.6} />
            <span className="text-[10px] font-body">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}
